/**
 * TD - Multi-step email + SMS capture popup.
 *
 * Opens a native <dialog> after a configurable delay, walks the visitor through
 * email capture -> SMS capture -> success, and records a suppression flag so the
 * popup stays hidden for N days after a dismiss or a completed signup.
 *
 * No marketing-platform API keys live here, by design. A Shopify theme is public source,
 * so any key in it is readable by anyone viewing the page — and both platforms already
 * offer a first-party path that needs no secret.
 *
 * Email submits the native Shopify customer form, so no key is involved. Shopify records
 * the address and consent, and the Omnisend app syncs it onward on customers/create.
 *
 * That has to be a real form post, not a fetch: Shopify's spam protection attaches a token
 * during submission, and an equivalent fetch is refused with a 400 without it. The page
 * therefore reloads mid-flow — and may detour via a captcha page first — so a
 * sessionStorage record is what carries the visitor back to the step they were heading to.
 * Shopify marks the accepted page with customer_posted=true.
 *
 * SMS opt-ins go through the Postscript SDK that their Shopify app already loads on every
 * page. It identifies the store by its public shop id, so no Postscript key is configured
 * here — and none can be: their keyed admin API sends no CORS headers and rejects the
 * preflight, so it is unreachable from a browser. The SDK path is what their own popups
 * use, and it reports itself as "3rd Party Onsite Opt-in".
 *
 * That opt-in is a two-pass flow: optIn() has Postscript text a verification code, the
 * visitor types it into the same step, and verify() completes the subscription.
 *
 * Rendered by sections/td-popup-email-capture.liquid.
 */
(function () {
  if (customElements.get('td-popup-email-capture')) return;

  // Fired on document by any on-demand entry point (currently the footer overlay links
  // block) to open the popup regardless of delay or suppression.
  var OPEN_EVENT = 'td:popup-email-capture:open';
  var RESUME_KEY = 'td-popup-email-capture-resume';
  var POSTED_PARAMETER = 'customer_posted';
  // Shopify sends form posts it finds suspicious through a full-page captcha first.
  var CHALLENGE_PATH = '/challenge';
  // How long a submission waits to be resumed before it counts as abandoned. Generous,
  // because the visitor may be solving a captcha in between.
  var RESUME_TTL = 15 * 60 * 1000;
  var STORAGE_KEY = 'td-popup-email-capture';
  // Written instead of a dated expiry once a visitor finishes the flow.
  var PERMANENT_EXPIRY = Number.MAX_SAFE_INTEGER;
  var PREVIEW_QUERY_PARAMETER = 'td_popup_preview';
  var FRAME_NAMES = ['email', 'sms', 'success'];
  // Theme editor only. Every setting change re-renders the section, so this carries the
  // previous render's frame markup and preview setting across to the replacement element.
  var editorState = null;
  var CLOSING_CLASS = 'dialog-closing';
  // How long to wait for the Postscript app to finish loading its SDK before giving up.
  var SDK_TIMEOUT = 8000;
  // Consentmo's cookie banner. The popup is a modal <dialog>, which sits above every
  // z-index and makes the rest of the page inert, so opening over the banner would hide it
  // and stop the visitor answering it. The automatic open waits for the banner instead.
  var CONSENT_HOST = 'csm-cookie-consent';
  // The banner and its preferences panel are tall; the collapsed re-open toggle is not.
  // Same threshold assets/td-overlay-links.js uses to tell them apart.
  var CONSENT_PANEL_MIN_HEIGHT_PX = 200;
  // Consentmo fires no event this theme can rely on and renders in a shadow root, so the
  // banner is polled. The pause keeps the popup from landing the instant the banner goes.
  var CONSENT_POLL_MS = 500;
  var AFTER_CONSENT_DELAY_MS = 1200;
  var SELECTORS = {
    dialog: '[data-td-popup-dialog]',
    frame: '[data-td-popup-frame]',
    close: '[data-td-popup-close]',
    skip: '[data-td-popup-skip]',
    submit: '[data-td-popup-submit]',
    successAction: '[data-td-popup-success-action]',
    launcher: '[data-td-popup-launcher]',
    error: '[data-td-popup-error]',
    otp: '[data-td-popup-otp]',
    resend: '[data-td-popup-resend]',
  };

  class TdPopupEmailCapture extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector(SELECTORS.dialog);
      if (!this.dialog) return;

      this.frames = {};
      var frames = this.querySelectorAll(SELECTORS.frame);
      for (var i = 0; i < frames.length; i++) {
        this.frames[frames[i].dataset.tdPopupFrame] = frames[i];
      }

      this.emailForm = this.frames.email ? this.frames.email.querySelector('form') : null;
      this.smsForm = this.frames.sms ? this.frames.sms.querySelector('form') : null;

      this.tag = this.dataset.contactTag || '';
      this.smsEnabled = this.dataset.smsEnabled === 'true';

      // Set once Postscript has texted a code and is waiting on the verify call.
      this.awaitingCode = false;
      this.phone = '';

      // Postscript is the only place an SMS opt-in can be stored, so resolve its SDK up
      // front and hide the step entirely when it never arrives, rather than collecting a
      // number with nowhere to put it. Usually settled long before the email step is done.
      this.smsAvailable = true;
      if (this.smsEnabled) {
        this.postscript().then((sdk) => {
          this.smsAvailable = Boolean(sdk);
        });
      }
      this.dialCode = this.dataset.dialCode || '+1';
      this.errorMessage = this.dataset.errorMessage || '';
      this.email = '';

      // In the theme editor the merchant needs to see the popup while configuring it,
      // so suppression and the full delay are both skipped.
      this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.previewFrame = this.getPreviewFrame();
      this.isPreview = Boolean(this.previewFrame);

      this.handleClose = this.handleClose.bind(this);
      this.handleSkip = this.handleSkip.bind(this);
      this.handleCancel = this.handleCancel.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleEmailSubmit = this.handleEmailSubmit.bind(this);
      this.handleSmsSubmit = this.handleSmsSubmit.bind(this);
      this.handleResend = this.handleResend.bind(this);
      this.handleSuccessAction = this.handleSuccessAction.bind(this);
      this.handleRequestOpen = this.handleRequestOpen.bind(this);
      this.handleLauncherClick = this.handleLauncherClick.bind(this);

      // The theme editor can move a section, which disconnects and reconnects this
      // element. Every listener below hangs off one abort signal so a second connect
      // cannot stack duplicates on children that survive the move.
      this.controller = new AbortController();
      var signal = this.controller.signal;

      this.launcher = this.querySelector(SELECTORS.launcher);
      if (this.launcher) this.launcher.addEventListener('click', this.handleLauncherClick, { signal });

      var closeButtons = this.querySelectorAll(SELECTORS.close);
      for (var c = 0; c < closeButtons.length; c++) {
        closeButtons[c].addEventListener('click', this.handleClose, { signal });
      }

      var skipButton = this.querySelector(SELECTORS.skip);
      if (skipButton) skipButton.addEventListener('click', this.handleSkip, { signal });

      var successAction = this.querySelector(SELECTORS.successAction);
      if (successAction) successAction.addEventListener('click', this.handleSuccessAction, { signal });

      this.dialog.addEventListener('cancel', this.handleCancel, { signal });
      this.dialog.addEventListener('click', this.handleBackdropClick, { signal });
      if (this.emailForm) this.emailForm.addEventListener('submit', this.handleEmailSubmit, { signal });
      if (this.smsForm) this.smsForm.addEventListener('submit', this.handleSmsSubmit, { signal });

      var resendButton = this.querySelector(SELECTORS.resend);
      if (resendButton) resendButton.addEventListener('click', this.handleResend, { signal });

      // Registered ahead of every early return below, so the footer entry point can still
      // open the popup for a visitor who is suppressed, mid-resume, or has auto-open off.
      // Suppression is a rule about the automatic open only.
      document.addEventListener(OPEN_EVENT, this.handleRequestOpen, { signal });

      // Shopify's captcha interstitial is a hop in the middle of a signup, not a page the
      // popup belongs on. No dialog and no reopen tab.
      if (window.location.pathname.indexOf(CHALLENGE_PATH) === 0) return;

      // Ahead of the suppression check: a visitor mid-signup must get the popup back,
      // even though submitting the email already recorded a suppression. The tab stays
      // hidden on this path — resume() either reopens the dialog itself, or the signup is
      // still in flight and a reopen tab would only compete with it.
      if (!this.isDesignMode && !this.isPreview && this.resume()) return;

      var suppressed = !this.isDesignMode && !this.isPreview && this.isSuppressed();

      if (this.isPreview) this.showFrame(this.previewFrame);

      // A suppressed visitor gets no auto-open, but still needs the reopen tab.
      if (suppressed) {
        this.updateLauncher();
        return;
      }

      // Auto-open is the merchant's call: with the footer entry point and the reopen tab
      // both in place, the popup can be made on-demand only without losing the capture
      // flow. The tab is shown here because nothing else is about to open the dialog.
      if (this.dataset.autoOpen === 'false' && !this.isDesignMode && !this.isPreview) {
        this.updateLauncher();
        return;
      }

      // Left hidden here on purpose: an unsuppressed visitor is about to get the
      // dialog itself, and the tab flashing first would read as a second widget.
      // The theme editor and QA previews open straight away, banner or not, so the
      // merchant can see what they are configuring.
      if (this.isDesignMode || this.isPreview) {
        this.timer = setTimeout(() => this.open(), 0);
        return;
      }

      this.timer = setTimeout(() => this.openAfterConsent(), Number(this.dataset.delay) || 0);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.stopConsentWait();
      if (this.controller) this.controller.abort();

      // Only give up the lock if this instance held it — in the theme editor a
      // replacement element can already be open by the time this fires.
      if (this.dialog && this.dialog.open) this.releaseScrollLock();
    }

    /* --- open / close --- */

    open() {
      if (!this.dialog || this.dialog.open) return;

      this.dialog.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
      this.updateLauncher();
    }

    /**
     * The automatic open, held back while Consentmo's banner or preferences panel is on
     * screen so the visitor can answer it first. Opens once it has gone.
     */
    openAfterConsent() {
      if (!this.consentBannerShowing()) {
        this.open();
        return;
      }

      this.consentTimer = setInterval(() => {
        if (this.consentBannerShowing()) return;

        this.stopConsentWait();
        this.timer = setTimeout(() => this.open(), AFTER_CONSENT_DELAY_MS);
      }, CONSENT_POLL_MS);
    }

    stopConsentWait() {
      clearInterval(this.consentTimer);
      this.consentTimer = null;
    }

    /**
     * @returns {boolean} True while Consentmo's banner or preferences panel is rendered.
     */
    consentBannerShowing() {
      var host = document.querySelector(CONSENT_HOST);
      if (!host || !host.shadowRoot) return false;

      var nodes = host.shadowRoot.querySelectorAll('*');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].offsetWidth && nodes[i].offsetHeight >= CONSENT_PANEL_MIN_HEIGHT_PX) return true;
      }

      return false;
    }

    async close() {
      if (!this.dialog.open) return;

      this.dialog.classList.add(CLOSING_CLASS);
      await this.animationEnd(this.dialog);
      this.dialog.close();
      this.dialog.classList.remove(CLOSING_CLASS);
      this.releaseScrollLock();
      this.updateLauncher();
    }

    /**
     * The tab is the only route back in after a dismissal, so it shows whenever the
     * dialog is closed. Visitors who finished the flow are excluded — they have
     * already claimed the offer and should not be pitched it again.
     */
    updateLauncher() {
      if (!this.launcher) return;

      this.launcher.hidden = this.dialog.open || this.isPermanentlySuppressed();
    }

    handleLauncherClick() {
      this.open();
    }

    releaseScrollLock() {
      document.documentElement.removeAttribute('scroll-lock');
    }

    /**
     * Returns an explicitly requested QA frame. The query parameter takes precedence
     * so developers can inspect a live theme without saving a theme-editor setting.
     * The "Preview slide" setting only applies in the theme editor: on the storefront
     * it would put every visitor straight onto that slide, skipping delay and suppression.
     *
     * @returns {string}
     */
    getPreviewFrame() {
      var urlPreview = new URLSearchParams(window.location.search).get(PREVIEW_QUERY_PARAMETER);
      var editorPreview = this.isDesignMode ? this.getEditedFrame() || this.dataset.previewFrame : '';
      var requested = urlPreview || editorPreview || '';

      return FRAME_NAMES.indexOf(requested) > -1 ? requested : '';
    }

    /**
     * Theme editor only. Returns the step whose markup changed since the previous render,
     * so adjusting a step's setting shows that step even when another slide is selected
     * for preview. Falls back to the step it last followed. A changed "Preview slide"
     * setting resets this so the merchant's pick wins.
     *
     * @returns {string}
     */
    getEditedFrame() {
      var markup = {};
      FRAME_NAMES.forEach((name) => {
        markup[name] = this.frames[name] ? this.frames[name].outerHTML : '';
      });

      var setting = this.dataset.previewFrame || '';
      var previous = editorState;
      var frame = '';

      if (previous && previous.setting === setting) {
        frame = FRAME_NAMES.find((name) => markup[name] !== previous.markup[name]) || previous.frame;
      }

      editorState = { markup: markup, setting: setting, frame: frame };
      return frame;
    }

    /**
     * Resolves once the dialog's closing animation finishes, or immediately when
     * the element is not animated (reduced motion, or animation already done).
     *
     * @param {HTMLElement} element
     * @returns {Promise<void>}
     */
    animationEnd(element) {
      var animations = element.getAnimations();
      if (!animations.length) return Promise.resolve();

      return Promise.allSettled(animations.map((animation) => animation.finished)).then(() => undefined);
    }

    /**
     * Opens on request from an on-demand entry point. Suppression is a rule about the
     * automatic open only, so a visitor who asks for the popup always gets it, and always
     * from the first frame rather than wherever a previous visit left off.
     *
     * A popup that is already on screen is left exactly as it is: resetting the frame
     * under a visitor who is mid-flow would throw away what they have typed.
     */
    handleRequestOpen() {
      clearTimeout(this.timer);
      this.stopConsentWait();
      if (!this.dialog || this.dialog.open) return;

      this.showFrame('email');
      this.open();
    }

    handleClose() {
      this.suppress();
      this.close();
    }

    handleCancel(event) {
      // Route the Escape key through the animated close path.
      event.preventDefault();
      this.handleClose();
    }

    handleBackdropClick(event) {
      // The dialog element is the visible panel, so a click is only on the backdrop
      // when it lands outside the panel's box.
      var rect = this.dialog.getBoundingClientRect();
      var outside =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;

      if (outside) this.handleClose();
    }

    handleSkip() {
      this.showFrame('success');
      this.suppress();
    }

    async handleSuccessAction(event) {
      event.preventDefault();

      // Completing the flow should take the visitor to Shop All without reopening
      // this campaign on future pages or visits.
      this.suppress(true);
      await this.close();
      window.location.assign(event.currentTarget.href);
    }

    /* --- frames --- */

    /**
     * @param {string} name - Frame key: 'email', 'sms' or 'success'.
     */
    showFrame(name) {
      var target = this.frames[name] ? name : 'success';

      // Keep the SMS markup available for visual QA, while regular visitors skip it
      // when the merchant has not connected an SMS list.
      var smsUsable = this.smsEnabled && this.smsAvailable;
      if (target === 'sms' && !smsUsable && !this.isPreview) target = 'success';

      Object.keys(this.frames).forEach((key) => {
        this.frames[key].hidden = key !== target;
      });
    }

    /* --- submissions --- */

    handleEmailSubmit() {
      var input = this.emailForm.querySelector('input[type="email"]');
      if (!input || !input.value.trim()) return;

      // Deliberately not intercepted. Shopify's customer form only accepts a real form
      // navigation — an equivalent fetch is refused with a 400 whatever it carries, since
      // it arrives without the token Shopify's spam protection attaches. So the form posts
      // normally and the page reloads; what survives the reload is this record, which
      // reopens the popup on the step the visitor was heading to.
      this.remember(input.value.trim());
    }

    /**
     * Stores the in-flight signup so the reload can pick the flow back up.
     *
     * @param {string} email
     */
    remember(email) {
      try {
        sessionStorage.setItem(RESUME_KEY, JSON.stringify({ email: email, at: Date.now() }));
      } catch (error) {
        /* Private mode: the signup still reaches Shopify, only the resume is lost. */
      }
    }

    /**
     * @returns {{email: string, at: number}|null}
     */
    readResume() {
      try {
        var stored = sessionStorage.getItem(RESUME_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch (error) {
        return null;
      }
    }

    clearResume() {
      try {
        sessionStorage.removeItem(RESUME_KEY);
      } catch (error) {
        /* Nothing to do — the record expires with the session anyway. */
      }
    }

    /**
     * Reopens the popup after the customer form posted, on the step the visitor was on
     * their way to.
     *
     * Only customer_posted=true confirms Shopify accepted the address, so the record is
     * left alone until then. A submission Shopify decides to challenge lands on an
     * interstitial captcha page first, and consuming the record there would strand the
     * visitor mid-signup — this waits it out instead, up to RESUME_TTL.
     *
     * @returns {boolean} Whether this took over the popup's normal behaviour.
     */
    resume() {
      var pending = this.readResume();
      if (!pending) return false;

      if (Date.now() - (pending.at || 0) > RESUME_TTL) {
        this.clearResume();
        return false;
      }

      var accepted = new URLSearchParams(window.location.search).get(POSTED_PARAMETER) === 'true';

      // Still in flight — most likely mid-captcha. Stay out of the way.
      if (!accepted) return true;

      this.clearResume();
      this.email = pending.email;
      this.identify();
      this.showFrame('sms');
      this.open();
      this.suppress();

      return true;
    }

    /**
     * Best-effort nudge so Omnisend can attach the session to the contact Shopify just
     * created, rather than waiting on the next page view.
     *
     * Deliberately not the delivery mechanism: Omnisend gates this on cookie consent and
     * silently drops it when consent is absent, which would lose signups from anyone who
     * has not accepted the banner. Shopify is what actually records the address.
     */
    identify() {
      if (!this.email) return;

      try {
        window.omnisend = window.omnisend || [];
        window.omnisend.push(['identifyContact', { email: this.email }]);
      } catch (error) {
        /* Tracking is never worth failing a signup over. */
      }
    }

    async handleSmsSubmit(event) {
      event.preventDefault();

      // Second pass: Postscript has texted a code and is waiting on it.
      if (this.awaitingCode) {
        var codeInput = this.smsForm.querySelector('input[name="otp"]');
        var code = codeInput ? codeInput.value.trim() : '';
        if (code) await this.verifyCode(code);
        return;
      }

      var input = this.smsForm.querySelector('input[type="tel"]');
      var phone = this.normalizePhone(input ? input.value : '');

      // Empty fields are caught by native validation, so this is an unparseable number.
      if (!phone) {
        this.setError(this.smsForm, this.errorMessage);
        return;
      }

      this.phone = phone;

      await this.optIn(phone);
    }

    /**
     * Phase one of the SMS step: asks Postscript to text a verification code.
     *
     * Postscript's SDK is already on the page via their Shopify app and identifies the
     * store by its public shop id, so no key is configured here. Their admin API (the one
     * needing a secret key) is not usable from a browser at all.
     *
     * @param {string} phone - E.164 number.
     */
    async optIn(phone) {
      var sdk = await this.postscript();

      // Postscript unavailable: the number is already saved to Omnisend above, so finish
      // rather than showing an error for something the visitor cannot fix.
      if (!sdk) {
        this.showFrame('success');
        this.suppress();
        return;
      }

      // Read by the SDK and attached to the opt-in, linking the number to the address
      // captured on the first step.
      if (this.email) window.ps__email = this.email;

      await this.submit(this.smsForm, async () => {
        // Failure can arrive either way: a thrown error, or a resolved { success: false }.
        var started = await sdk.acquisition.optIn(phone, {
          source: this.tag || 'Website popup',
          sourceName: this.tag || 'Website popup',
        });
        if (started && started.success === false) throw new Error('Opt-in refused');

        this.requestCode();
        return { ok: true, stay: true };
      });
    }

    /**
     * Phase two: verifies the texted code. The SDK holds the pending number and token
     * from the opt-in call, so only the code is passed.
     *
     * @param {string} code
     */
    async verifyCode(code) {
      var sdk = await this.postscript();
      if (!sdk) return;

      await this.submit(this.smsForm, async () => {
        // Both values go in one options object. Passing them positionally is accepted
        // silently but sends neither, leaving the request with only the shop id.
        var result = await sdk.acquisition.verify({ phoneNumber: this.phone, code: code });
        if (result && result.success === false) throw new Error('Verification failed');
        return { ok: true };
      });
    }

    /** Reveals the code field and switches the submit button over to verifying. */
    requestCode() {
      this.awaitingCode = true;

      var otp = this.smsForm.querySelector(SELECTORS.otp);
      var input = otp ? otp.querySelector('input') : null;
      var phoneInput = this.smsForm.querySelector('input[type="tel"]');
      var button = this.smsForm.querySelector(SELECTORS.submit);

      if (otp) otp.hidden = false;
      if (input) {
        input.disabled = false;
        input.required = true;
        input.focus();
      }
      // Stops the number being edited while a code for it is outstanding.
      if (phoneInput) phoneInput.readOnly = true;
      if (button && button.dataset.labelVerify) button.textContent = button.dataset.labelVerify;
    }

    /** Asks Postscript to text the code again. */
    async handleResend() {
      var sdk = await this.postscript();
      if (!sdk || !this.phone) return;

      try {
        await sdk.acquisition.resendOtp(this.phone);
        this.setError(this.smsForm, '');
      } catch (error) {
        this.setError(this.smsForm, this.errorMessage);
      }
    }

    /**
     * Runs a subscription request with button/error state handling, then advances
     * to the next frame on success.
     *
     * @param {HTMLFormElement} form
     * @param {() => Promise<Response>} request
     */
    async submit(form, request) {
      var button = form.querySelector(SELECTORS.submit);

      this.setError(form, '');
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }

      try {
        var response = await request();

        // A step that is mid-flow (Postscript waiting on its code) keeps the frame up.
        if (response.stay) return;

        this.showFrame(form === this.emailForm ? 'sms' : 'success');
        this.suppress();
      } catch (error) {
        this.setError(form, this.errorMessage);
      } finally {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }

    /**
     * Resolves the Postscript SDK, which the Postscript app loads asynchronously on every
     * page. Returns null when it never arrives, so the SMS step can degrade instead of
     * trapping the visitor.
     *
     * @returns {Promise<object|null>}
     */
    postscript() {
      var deadline = Date.now() + SDK_TIMEOUT;

      return new Promise(function (resolve) {
        (function poll() {
          var sdk = window.postscript;
          if (sdk && sdk.acquisition && typeof sdk.acquisition.optIn === 'function') {
            resolve(sdk);
            return;
          }
          if (Date.now() > deadline) {
            resolve(null);
            return;
          }
          setTimeout(poll, 100);
        })();
      });
    }

    /**
     * Converts a typed number to the E.164 form Omnisend requires.
     *
     * @param {string} value
     * @returns {string} E.164 number, or an empty string when unusable.
     */
    normalizePhone(value) {
      var trimmed = String(value).trim();

      if (trimmed.charAt(0) === '+') {
        var international = trimmed.slice(1).replace(/\D/g, '');
        return international ? '+' + international : '';
      }

      var digits = trimmed.replace(/\D/g, '');
      if (!digits) return '';

      // Already carries the dial code without the plus, e.g. "1 555 012 3456".
      var codeDigits = this.dialCode.replace(/\D/g, '');
      if (codeDigits && digits.indexOf(codeDigits) === 0 && digits.length > 10) {
        return '+' + digits;
      }

      return this.dialCode + digits;
    }

    /**
     * @param {HTMLFormElement} form
     * @param {string} message - Empty string hides the message.
     */
    setError(form, message) {
      var target = form.querySelector(SELECTORS.error);
      if (!target) return;

      target.textContent = message || '';
      target.hidden = !message;
    }

    /* --- suppression --- */

    suppress(permanent) {
      if (this.isDesignMode || this.isPreview) return;

      var days = Number(this.dataset.suppressDays) || 30;
      var expiry = permanent ? PERMANENT_EXPIRY : Date.now() + days * 24 * 60 * 60 * 1000;

      try {
        localStorage.setItem(STORAGE_KEY, String(expiry));
      } catch (error) {
        // Private browsing or a full quota — the popup simply shows again next visit.
      }
    }

    /**
     * @returns {boolean}
     */
    isSuppressed() {
      try {
        var expiry = Number(localStorage.getItem(STORAGE_KEY));
        return Boolean(expiry) && expiry > Date.now();
      } catch (error) {
        return false;
      }
    }

    /**
     * True only for visitors who completed the flow, as opposed to the dated
     * suppression a dismissal writes.
     *
     * @returns {boolean}
     */
    isPermanentlySuppressed() {
      try {
        return Number(localStorage.getItem(STORAGE_KEY)) === PERMANENT_EXPIRY;
      } catch (error) {
        return false;
      }
    }
  }

  customElements.define('td-popup-email-capture', TdPopupEmailCapture);
})();
